/**
 * DelegacionesTab — pestaña de delegaciones de la empresa matriz.
 *
 * Lista las delegaciones (entidades hijas) con sus flags de depósito
 * y almacén logístico. Incluye modal inline para crear/editar delegaciones
 * sin salir de la ficha de la empresa matriz.
 */
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  GitBranch, Warehouse, Truck, Plus, Users,
  ExternalLink, Loader2, MapPin, X, Search,
} from 'lucide-react';
import { municipiosApi, req } from '../../services/api';
import { toast } from 'sonner';
import { Tree } from 'react-organizational-chart';
import { OrgCard, OrgBranch } from './_shared';

// ─── KPI chip ─────────────────────────────────────────────────────────────
const Chip = ({ icon: Icon, label, value, color }) => (
  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${color}`}>
    <Icon className="w-4 h-4 shrink-0" />
    <div>
      <p className="text-xs font-semibold leading-none">{value}</p>
      <p className="text-[10px] opacity-70 mt-0.5">{label}</p>
    </div>
  </div>
);

// ─── Toggle flag card ──────────────────────────────────────────────────────
const FlagToggle = ({ icon: Icon, label, sublabel, checked, onChange, color }) => (
  <label className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition select-none flex-1 min-w-[200px] ${
    checked ? color.active : 'border-ink-200 bg-white text-ink-600 hover:border-ink-300'
  }`}>
    <input type="checkbox" className={color.accent} checked={checked} onChange={onChange} />
    <Icon className="w-4 h-4 shrink-0" />
    <div>
      <p className="text-sm font-medium leading-none">{label}</p>
      <p className="text-[11px] mt-0.5 opacity-70">{sublabel}</p>
    </div>
  </label>
);

const emptyForm = (parentNombre = '') => ({
  razon_social: parentNombre ? `${parentNombre} - Delegación` : '',
  direccion: '',
  codigo_postal: '',
  localidad: '',
  provincia: '',
  telefono: '',
  tiene_deposito_nuestro: false,
  es_almacen_logistico: false,
});

export default function DelegacionesTab({ entidadId, parentNombre = '' }) {
  const navigate = useNavigate();
  const [delegaciones, setDelegaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactosTodos, setContactosTodos] = useState([]);
  const [loadingContactos, setLoadingContactos] = useState(false);

  // Modal crear delegación
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(() => emptyForm(parentNombre));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Autocomplete de dirección
  const [dirSugerencias, setDirSugerencias] = useState([]);
  const [buscandoDir, setBuscandoDir] = useState(false);
  const dirTimer = useRef(null);

  // Autocomplete de municipio
  const [munSugerencias, setMunSugerencias] = useState([]);
  const [buscandoMun, setBuscandoMun] = useState(false);
  const munTimer = useRef(null);

  // Lookup por CP
  const [buscandoCp, setBuscandoCp] = useState(false);

  const buscarDireccion = useCallback((q) => {
    clearTimeout(dirTimer.current);
    if (q.trim().length < 5) { setDirSugerencias([]); return; }
    dirTimer.current = setTimeout(async () => {
      setBuscandoDir(true);
      try {
        const rs = await municipiosApi.autocompleteDireccion(q.trim());
        setDirSugerencias(rs || []);
      } catch (_) { setDirSugerencias([]); }
      finally { setBuscandoDir(false); }
    }, 500);
  }, []);

  const buscarMunicipio = useCallback((q) => {
    clearTimeout(munTimer.current);
    if (q.trim().length < 2) { setMunSugerencias([]); return; }
    munTimer.current = setTimeout(async () => {
      setBuscandoMun(true);
      try {
        const rs = await municipiosApi.list({ q: q.trim(), limit: 8 });
        setMunSugerencias(rs || []);
      } catch (_) { setMunSugerencias([]); }
      finally { setBuscandoMun(false); }
    }, 300);
  }, []);

  const lookupCp = useCallback(async (cp) => {
    if (!/^\d{4,6}$/.test(cp.trim())) return;
    setBuscandoCp(true);
    try {
      const r = await municipiosApi.porCp(cp.trim());
      if (r.localidad || r.provincia) {
        setForm((f) => ({
          ...f,
          localidad: r.localidad || f.localidad,
          provincia: r.provincia || f.provincia,
          codigo_postal: r.codigo_postal || cp,
        }));
      }
    } catch (_) {}
    finally { setBuscandoCp(false); }
  }, []);

  const cargarDelegaciones = () => {
    setLoading(true);
    req(`/api/entidades/${entidadId}/delegaciones`)
      .then(setDelegaciones)
      .catch(() => toast.error('Error cargando delegaciones'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { cargarDelegaciones(); /* eslint-disable-next-line */ }, [entidadId]);

  // Cargar contactos de la matriz + delegaciones para organigrama unificado
  useEffect(() => {
    setLoadingContactos(true);
    const ids = [entidadId, ...delegaciones.map((d) => d.id)];
    Promise.all(ids.map((id) =>
      req(`/api/contactos?entidad_id=${id}&limit=200`)
        .then((cs) => cs.map((c) => ({ ...c, _entidad_id: id })))
        .catch(() => [])
    ))
      .then((groups) => setContactosTodos(groups.flat()))
      .finally(() => setLoadingContactos(false));
  }, [entidadId, delegaciones]);

  const organigramaUnificado = useMemo(() => {
    if (contactosTodos.length === 0) return [];
    const byId = Object.fromEntries(contactosTodos.map((c) => [c.id, { ...c, children: [] }]));
    const roots = [];
    for (const c of Object.values(byId)) {
      if (c.jefe_directo_id && byId[c.jefe_directo_id]) {
        byId[c.jefe_directo_id].children.push(c);
      } else {
        roots.push(c);
      }
    }
    return roots;
  }, [contactosTodos]);

  const abrirModal = () => {
    setForm(emptyForm(parentNombre));
    setErr(''); setDirSugerencias([]); setMunSugerencias([]);
    setModalOpen(true);
  };
  const cerrarModal = () => { setModalOpen(false); setErr(''); setDirSugerencias([]); setMunSugerencias([]); };

  const guardar = async () => {
    if (!form.razon_social.trim()) { setErr('La razón social es obligatoria'); return; }
    setSaving(true); setErr('');
    try {
      await req('/api/entidades', {
        method: 'POST',
        body: JSON.stringify({
          razon_social: form.razon_social.trim(),
          direccion: form.direccion.trim() || undefined,
          codigo_postal: form.codigo_postal.trim() || undefined,
          localidad: form.localidad.trim() || undefined,
          provincia: form.provincia.trim() || undefined,
          telefono: form.telefono.trim() || undefined,
          tipo: 'otro',
          estado: 'activo',
          es_cliente: true,
          es_delegacion: true,
          parent_entidad_id: entidadId,
          tiene_deposito_nuestro: form.tiene_deposito_nuestro,
          es_almacen_logistico: form.es_almacen_logistico,
        }),
      });
      toast.success(`Delegación "${form.razon_social}" creada`);
      cerrarModal();
      cargarDelegaciones();
    } catch (e) {
      setErr(e.message || 'Error al crear la delegación');
    } finally {
      setSaving(false);
    }
  };

  const kpis = {
    total: delegaciones.length,
    conDeposito: delegaciones.filter((d) => d.tiene_deposito_nuestro).length,
    almacenLogistico: delegaciones.filter((d) => d.es_almacen_logistico).length,
  };

  if (loading) {
    return (
      <div className="py-16 text-center" data-testid="delegaciones-loading">
        <Loader2 className="w-6 h-6 animate-spin text-brand-600 mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="delegaciones-tab">

      {/* KPIs + botón nueva delegación */}
      <div className="flex flex-wrap items-center gap-3">
        <Chip icon={GitBranch} label="Delegaciones" value={kpis.total}
          color="border-brand-200 bg-brand-50 text-brand-700" />
        <Chip icon={Warehouse} label="Con depósito nuestro" value={kpis.conDeposito}
          color="border-amber-200 bg-amber-50 text-amber-700" />
        <Chip icon={Truck} label="Almacén logístico" value={kpis.almacenLogistico}
          color="border-blue-200 bg-blue-50 text-blue-700" />
        <div className="ml-auto">
          <Button size="sm" className="bg-brand-500 hover:bg-brand-600 text-white"
            onClick={abrirModal} data-testid="btn-nueva-delegacion">
            <Plus className="w-4 h-4 mr-1.5" /> Nueva delegación
          </Button>
        </div>
      </div>

      {/* Lista delegaciones */}
      {delegaciones.length === 0 ? (
        <Card className="p-10 text-center text-ink-400 border-dashed border-ink-200">
          <GitBranch className="w-8 h-8 mx-auto mb-2 text-ink-300" />
          <p className="text-sm font-medium">Sin delegaciones registradas</p>
          <p className="text-xs mt-1">Crea la primera usando el botón "Nueva delegación".</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {delegaciones.map((d) => (
            <Card key={d.id} className="p-4 border-ink-200 bg-white hover:shadow-sm transition"
              data-testid={`delegacion-${d.id}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-ink-900 truncate">{d.razon_social}</p>
                    {d.cif && <span className="text-[10px] text-ink-400">{d.cif}</span>}
                  </div>
                  {(d.localidad || d.provincia) && (
                    <p className="text-[11px] text-ink-500 flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" />
                      {[d.localidad, d.provincia].filter(Boolean).join(', ')}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {d.tiene_deposito_nuestro && (
                      <Badge className="bg-amber-50 text-amber-700 border-amber-200 border text-[10px] flex items-center gap-1">
                        <Warehouse className="w-3 h-3" /> Depósito nuestro
                      </Badge>
                    )}
                    {d.es_almacen_logistico && (
                      <Badge className="bg-blue-50 text-blue-700 border-blue-200 border text-[10px] flex items-center gap-1">
                        <Truck className="w-3 h-3" /> Almacén logístico
                      </Badge>
                    )}
                    {d.num_contactos > 0 && (
                      <Badge className="bg-ink-50 text-ink-600 border-ink-200 border text-[10px] flex items-center gap-1">
                        <Users className="w-3 h-3" /> {d.num_contactos} contacto{d.num_contactos > 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/clientes/${d.id}`)}
                  className="p-1.5 rounded hover:bg-brand-50 text-brand-600 shrink-0"
                  title="Abrir ficha completa de esta delegación"
                  data-testid={`btn-abrir-delegacion-${d.id}`}
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Organigrama unificado */}
      {organigramaUnificado.length > 0 && (
        <Card className="p-5 border-ink-200 bg-white" data-testid="delegaciones-organigrama">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-ink-800 flex items-center gap-2">
              <Users className="w-4 h-4 text-brand-600" /> Organigrama unificado
            </h3>
            <span className="text-xs text-ink-400">
              {contactosTodos.length} contacto{contactosTodos.length !== 1 ? 's' : ''} en toda la estructura
            </span>
          </div>
          {loadingContactos ? (
            <div className="py-6 text-center"><Loader2 className="w-4 h-4 animate-spin text-brand-600 mx-auto" /></div>
          ) : (
            <div className="overflow-x-auto pb-3">
              {organigramaUnificado.length === 1 ? (
                <Tree lineWidth="2px" lineColor="#cbd5e1" lineBorderRadius="6px"
                  label={<OrgCard contacto={organigramaUnificado[0]} onClick={() => {}} />}>
                  {organigramaUnificado[0].children.map((child) => (
                    <OrgBranch key={child.id} node={child} onClick={() => {}} />
                  ))}
                </Tree>
              ) : (
                <div className="flex flex-wrap gap-6">
                  {organigramaUnificado.map((root) => (
                    <Tree key={root.id} lineWidth="2px" lineColor="#cbd5e1" lineBorderRadius="6px"
                      label={<OrgCard contacto={root} onClick={() => {}} />}>
                      {root.children.map((child) => (
                        <OrgBranch key={child.id} node={child} onClick={() => {}} />
                      ))}
                    </Tree>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* ─── Modal: crear delegación ──────────────────────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onMouseDown={(e) => { if (e.target === e.currentTarget) cerrarModal(); }}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full"
            onMouseDown={(e) => e.stopPropagation()}
            data-testid="modal-nueva-delegacion">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200">
              <h2 className="font-heading font-semibold text-ink-900 flex items-center gap-2">
                <GitBranch className="w-5 h-5 text-brand-600" /> Nueva delegación
              </h2>
              <button onClick={cerrarModal} className="text-ink-400 hover:text-ink-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Formulario */}
            <div className="px-6 py-4 space-y-3">
              {/* Razón social — pre-rellenada con sugerencia del padre */}
              <div>
                <label className="text-xs font-medium text-ink-600 block mb-1">Razón social *</label>
                <Input
                  value={form.razon_social}
                  onChange={(e) => setForm({ ...form, razon_social: e.target.value })}
                  placeholder={parentNombre ? `${parentNombre} - Delegación...` : 'Nombre de la delegación'}
                  autoFocus
                  data-testid="deleg-razon-social"
                />
                {parentNombre && !form.razon_social && (
                  <button type="button" className="text-[10px] text-brand-600 hover:underline mt-0.5"
                    onClick={() => setForm({ ...form, razon_social: `${parentNombre} - Delegación` })}>
                    Usar sugerencia: "{parentNombre} - Delegación"
                  </button>
                )}
              </div>

              {/* Dirección con autocomplete Nominatim */}
              <div className="relative">
                <label className="text-xs font-medium text-ink-600 block mb-1">
                  Dirección <span className="text-ink-400 font-normal">(calle, número…)</span>
                </label>
                <div className="relative">
                  <Input
                    value={form.direccion}
                    onChange={(e) => { setForm({ ...form, direccion: e.target.value }); buscarDireccion(e.target.value); }}
                    placeholder="Ej: Calle Mayor 5, Barcelona"
                    data-testid="deleg-direccion"
                  />
                  {buscandoDir && <Loader2 className="absolute right-2 top-2.5 w-4 h-4 animate-spin text-ink-400" />}
                </div>
                {dirSugerencias.length > 0 && (
                  <div className="absolute top-full left-0 right-0 z-[9999] bg-white border border-ink-200 rounded-lg shadow-lg mt-0.5 max-h-52 overflow-y-auto">
                    {dirSugerencias.map((s, i) => (
                      <button key={i} type="button"
                        className="w-full text-left px-3 py-2 text-xs hover:bg-brand-50 border-b border-ink-50 last:border-0"
                        onClick={() => {
                          const dir = [s.calle, s.numero].filter(Boolean).join(' ');
                          setForm((f) => ({
                            ...f,
                            direccion: dir || s.display.split(',')[0],
                            localidad: s.localidad || f.localidad,
                            provincia: s.provincia || f.provincia,
                            codigo_postal: s.codigo_postal || f.codigo_postal,
                          }));
                          setDirSugerencias([]);
                        }}>
                        <p className="font-medium text-ink-800 truncate">{[s.calle, s.numero].filter(Boolean).join(' ') || s.display.split(',')[0]}</p>
                        <p className="text-ink-400 truncate">{[s.codigo_postal, s.localidad, s.provincia].filter(Boolean).join(' · ')}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* CP + Localidad + Provincia en fila */}
              <div className="grid grid-cols-3 gap-2">
                <div className="relative">
                  <label className="text-xs font-medium text-ink-600 block mb-1">C.P.</label>
                  <div className="relative">
                    <Input
                      value={form.codigo_postal}
                      onChange={(e) => {
                        const v = e.target.value;
                        setForm((f) => ({ ...f, codigo_postal: v }));
                        if (v.trim().length >= 4) lookupCp(v);
                      }}
                      placeholder="08001"
                      maxLength={6}
                      data-testid="deleg-cp"
                    />
                    {buscandoCp && <Loader2 className="absolute right-1.5 top-2.5 w-3.5 h-3.5 animate-spin text-ink-400" />}
                  </div>
                </div>
                <div className="relative">
                  <label className="text-xs font-medium text-ink-600 block mb-1">Municipio</label>
                  <Input
                    value={form.localidad}
                    onChange={(e) => { setForm({ ...form, localidad: e.target.value }); buscarMunicipio(e.target.value); }}
                    placeholder="Barcelona"
                    data-testid="deleg-localidad"
                  />
                  {munSugerencias.length > 0 && (
                    <div className="absolute top-full left-0 right-0 z-[9999] bg-white border border-ink-200 rounded-lg shadow-lg mt-0.5 max-h-40 overflow-y-auto">
                      {munSugerencias.map((m) => (
                        <button key={m.codigo_ine} type="button"
                          className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-brand-50 border-b border-ink-50 last:border-0"
                          onClick={() => {
                            setForm((f) => ({ ...f, localidad: m.nombre, provincia: m.provincia }));
                            setMunSugerencias([]);
                          }}>
                          <span className="font-medium">{m.nombre}</span>
                          <span className="ml-1 text-ink-400">{m.provincia}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-ink-600 block mb-1">Provincia</label>
                  <Input value={form.provincia}
                    onChange={(e) => setForm({ ...form, provincia: e.target.value })}
                    placeholder="Auto"
                    data-testid="deleg-provincia"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-ink-600 block mb-1">Teléfono</label>
                <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                  placeholder="+34 93 000 00 00" data-testid="deleg-telefono" />
              </div>

              {/* Flags de almacén */}
              <div className="flex flex-wrap gap-2 pt-1">
                <FlagToggle
                  icon={Warehouse} label="Depósito nuestro"
                  sublabel="Tenemos stock AMD aquí"
                  checked={form.tiene_deposito_nuestro}
                  onChange={(e) => setForm({ ...form, tiene_deposito_nuestro: e.target.checked })}
                  color={{ active: 'border-amber-300 bg-amber-50 text-amber-800', accent: 'accent-amber-500' }}
                />
                <FlagToggle
                  icon={Truck} label="Almacén logístico"
                  sublabel="Distribuye a sus delegaciones"
                  checked={form.es_almacen_logistico}
                  onChange={(e) => setForm({ ...form, es_almacen_logistico: e.target.checked })}
                  color={{ active: 'border-blue-300 bg-blue-50 text-blue-800', accent: 'accent-blue-500' }}
                />
              </div>

              {err && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</p>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-ink-200 flex justify-end gap-2">
              <Button variant="outline" onClick={cerrarModal} disabled={saving}>Cancelar</Button>
              <Button
                className="bg-brand-500 hover:bg-brand-600 text-white"
                onClick={guardar} disabled={saving}
                data-testid="deleg-guardar"
              >
                {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Creando…</> : 'Crear delegación'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
